import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { filter } from 'lodash';
import { map as fpMap } from 'lodash/fp';
import React, { useContext, useEffect, useState } from 'react';

import CurrentUserContext from '../CurrentUser';

import FunnelContext from './Context';
import { type UploadedFile } from './types';

import {
  CreateNotesDocument,
  CreateProjectDocument,
  DestroyNotesDocument,
  GetClientsBasicDocument,
  type GetClientsBasicQuery,
  GetProjectCodeSuggestionDocument,
  GetProjectNotesDocument,
  GetProjectsForFunnelDocument,
  NotesTypeEnum,
  type Project,
  ProjectStatusEnum,
  UpdateNotesDocument,
  UploadFunnelProjectFilesDocument,
} from '@/graphql/generated/graphql';
import useDirectUpload from '@/hooks/useDirectUpload';
import { displayNotification } from '@/utils/notifications';

const mapNode = fpMap('node');

interface FunnelContextProviderProps {
  children: React.ReactNode;
}

const mapSignedBlobId = fpMap('signedBlobId');

const FunnelContextProvider = ({ children }: FunnelContextProviderProps) => {
  const { currentUser, currentCompany } = useContext(CurrentUserContext);
  const { uploadFile } = useDirectUpload();

  const [selectedClient, setSelectedClient] = useState<NonNullable<GetClientsBasicQuery['getClients']>[0] | undefined>(
    undefined,
  );
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [fileUploading, setFileUploading] = useState<boolean>(false);

  const { data: clients } = useQuery(GetClientsBasicDocument, {
    fetchPolicy: 'network-only',
    skip: !currentUser?.defaultCompany?.id,
  });

  const { data: projects, loading } = useQuery(GetProjectsForFunnelDocument, {
    skip: !selectedClient?.id,
    fetchPolicy: 'network-only',
    variables: {
      companyId: currentUser?.defaultCompany?.id as string,
      clientId: selectedClient?.id,
    },
  });

  const { data: getProjectNotes } = useQuery(GetProjectNotesDocument, {
    skip: !selectedProject,
    fetchPolicy: 'network-only',
    variables: { id: selectedProject?.id as string },
  });

  const [codeSuggestionQuery] = useLazyQuery(GetProjectCodeSuggestionDocument, {
    fetchPolicy: 'network-only',
  });

  const [uploadDocuments] = useMutation(UploadFunnelProjectFilesDocument, {
    refetchQueries: ['getProjectsForFunnel'],
  });

  const [updateNote] = useMutation(UpdateNotesDocument);

  const [destroyNotes] = useMutation(DestroyNotesDocument);

  const [createProject] = useMutation(CreateProjectDocument);

  const [createNote] = useMutation(CreateNotesDocument);

  const createProjectNote = (description: string) => {
    createNote({
      variables: {
        noteData: {
          description: description,
          notesType: NotesTypeEnum.Project,
          targetId: selectedProject?.id,
          targetType: 'Project',
        },
      },
      update(cache, { data }) {
        const createNotes = data?.createNotes;
        const existingData = cache.readQuery({
          query: GetProjectNotesDocument,
          variables: { id: selectedProject?.id as string },
        });

        if (existingData && createNotes) {
          cache.writeQuery({
            query: GetProjectNotesDocument,
            variables: { id: selectedProject?.id as string },
            data: {
              getProjectNotes: [...existingData.getProjectNotes, createNotes],
            },
          });
        }
      },
    });
  };

  const updateProjectNote = (id: string, description: string) => {
    updateNote({
      variables: {
        noteData: {
          id,
          description,
        },
      },
    });
  };

  const deleteProjectNote = (id: string) => {
    destroyNotes({
      variables: { id },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: 'Note', id }) });

        cache.gc();
      },
    });
  };

  const onCreateProject = () => {
    codeSuggestionQuery({ variables: { title: 'Untitled Project' } }).then((res) => {
      createProject({
        variables: {
          title: 'Untitled Project',
          code: res.data?.getProjectCodeSuggestion as string,
          companyId: currentCompany?.id as string,
          status: ProjectStatusEnum.NotStarted,
          visible: false,
          clientId: selectedClient?.id,
        },
        refetchQueries: ['getProjectsForFunnel'],
      });
    });
  };

  const onProjectSelect = (project: Project) => {
    setSelectedProject(project);
  };

  const onClientSelect = (client: NonNullable<GetClientsBasicQuery['getClients']>[0]) => {
    setSelectedClient(client);
    setSelectedProject(undefined);
    setFiles([]);
  };

  const onDocumentsSent = () => {
    displayNotification({ type: 'success', text: 'Files dispatched successfully.' });
    setSelectedClient(undefined);
    setSelectedProject(undefined);
    setFiles([]);
  };

  const onFileRemove = (fileId: string) => {
    const newFiles = filter(files, (file) => file.id !== fileId);

    setFiles(newFiles);

    uploadDocuments({
      variables: {
        id: selectedProject?.id as string,
        signedIds: mapSignedBlobId(newFiles),
      },
    });
  };

  const onFileUpload = async (uploadedFiles: File[]) => {
    setFileUploading(true);

    // 1. Create the placeholder objects
    const newFilesPlaceholder = uploadedFiles.map((file, index) => ({
      id: `file-${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadStatus: 'pending' as const,
    }));

    setFiles((prev) => [...prev, ...newFilesPlaceholder]);

    try {
      // 2. Map files to upload promises
      const uploadPromises = uploadedFiles.map(async (file, index) => {
        const fileId = newFilesPlaceholder[index].id;

        // Update status to 'uploading'
        setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, uploadStatus: 'uploading' } : f)));

        const result = await uploadFile(file, selectedProject?.code);

        // if (!result) throw new Error("Upload failed");
        if (!result) {
          return {
            ...newFilesPlaceholder[index],
            uploadStatus: 'error' as const,
            error: 'Upload failed',
          };
        }

        // Return the updated file object so we can use it after Promise.all
        return {
          ...newFilesPlaceholder[index],
          uploadStatus: 'completed' as const,
          signedBlobId: result.signedBlobId,
          previewUrl: result.previewUrl,
        };
      });

      // 3. Wait for all uploads to finish
      const completedFiles = await Promise.all(uploadPromises);

      // 4. Update the local state with all completed files
      setFiles((prev) => {
        return prev.map((f) => {
          const completed = completedFiles.find((cf) => cf.id === f.id);
          return completed ? completed : f;
        });
      });

      const failedFile = completedFiles.some((file) => file.uploadStatus === 'error');

      if (!failedFile) {
        await uploadDocuments({
          variables: {
            id: selectedProject?.id as string,
            signedIds: mapSignedBlobId([...files, ...completedFiles]),
          },
        });
      }
    } catch (error) {
      console.error('Batch upload failed', error);
    } finally {
      setFileUploading(false);
    }
  };

  useEffect(() => {
    if (!selectedProject) return;

    setFiles(
      selectedProject.attachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.filename,
        size: attachment.byteSize,
        type: '',
        uploadStatus: 'completed' as const,
        signedBlobId: attachment.signedBlobId ?? undefined,
        previewUrl: attachment.previewUrl,
      })),
    );
  }, [selectedProject]);

  const context = {
    files,
    notes: getProjectNotes?.getProjectNotes || [],
    loading,
    fileUploading,
    selectedClient,
    selectedProject,
    clients: clients?.getClients,
    projects: mapNode(projects?.getProjects?.edges),
    onFileRemove,
    onFileUpload,
    onClientSelect,
    onDocumentsSent,
    onCreateProject,
    onProjectSelect,
    createProjectNote,
    updateProjectNote,
    deleteProjectNote,
  };

  return <FunnelContext.Provider value={context}>{children}</FunnelContext.Provider>;
};

export default FunnelContextProvider;
